import {ChangeDetectionStrategy, Component, Injectable, input, isDevMode, output, Signal, signal} from "@angular/core";
import {SuggestionProps} from "@tiptap/suggestion";
import {ElasticSearchResultat, ElasticSearchTreff, GruppeId, OpplastetDokumentVersjonIndeksfelter, SakIndeksfelter, SøkEntitetSpesifikasjon, SøkEntitetType, SøkRessursClient} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {toObservable, toSignal} from "@angular/core/rxjs-interop";
import {debounceTime, filter, switchMap} from "rxjs";
import {erIkkeTomStreng} from "felles-ng2/støttefunksjoner/strenger";
import {TranslateModule} from "@ngx-translate/core";
import {MatTooltip} from "@angular/material/tooltip";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {MatIcon} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";
import {InternLenkeNodeAttr, InternLenkeType} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/utvidelser/InternLenke";

export type LeggTilInternLenkeType = 'sak' | 'dokument';

/** Komponent som viser gui for å legge til ny intern lenke, benyttes i oppsettet av Tiptaps {@link Mention}-extension
 * for å generer innholdet til tippy-instansen som vises når mention rendrer innhold.
 *
 * Komponenten får sine verdier satt av Mention ihht interface {@link SuggestionProps}.
 * Ideelt sett burde komponenten implementert dette interfacet, men pga bruk av angular input()
 * får vi typekonflikter og kan derfor ikke la komponent implementere dette interfacet.
 *
 * MERK at denne ikke kan benyttes utenfor tiptap editor
 * @Deprecated - internlenker håndteres av tiptap/utvidelser/Lenke.ts
 */
@Component({
    selector: 'riktekst-redigering-intern-lenke-finn',
    templateUrl: 'intern-lenke-finn.html',
    styleUrl: 'intern-lenke-finn.scss',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslateModule,
        MatTooltip,
        MatIcon,
        MatIconButton
    ]
})
export class RiktekstRedigeringInternLenkeFinnKomponent {

    /** Emitter når bruker har valgt treff */
    vedTreffValgt = output<InternLenkeNodeAttr>();

    lenketype = input.required<InternLenkeType>()
    /** Teksten som skal brukes i søk */
    soekestreng = input.required<string>();
    /** Gruppen søket skal begrenses til */
    gruppeId = input.required<GruppeId>();

    treff: Signal<ElasticSearchTreff<any>[]>;
    aktivTreffIndeks = signal(0);

    constructor(private avhengigheter: InternLenkeKomponentAvhengigheter, private oversettelser: Oversettelser) {
        this.treff = toSignal(toObservable(this.soekestreng)
            .pipe(
              filter(søkestreng => erIkkeTomStreng(søkestreng) && søkestreng.length > 2),
              debounceTime(200),
              switchMap(søkestreng => this.søk(søkestreng))),
          {initialValue: []});
    }

    hjelpetekstSak(innslag: ElasticSearchTreff<SakIndeksfelter>) {
        return this.lenketekstSak(innslag);
    }

    hjelpetekstDokument(innslag: ElasticSearchTreff<OpplastetDokumentVersjonIndeksfelter>) {
        return this.lenketekstDokument(innslag);
    }

    sakValgt(innslag: ElasticSearchTreff<SakIndeksfelter>) {
        const data: InternLenkeNodeAttr = {id: innslag._id, label: `${innslag._source.nummer} ${innslag._source.tittel}`};
        this.vedTreffValgt.emit(data);
    }

    dokumentValgt(innslag: ElasticSearchTreff<OpplastetDokumentVersjonIndeksfelter>) {
        const data: InternLenkeNodeAttr = {id: innslag._id, label: `${innslag._source.filnavn}`};
        this.vedTreffValgt.emit(data);
    }

    /** Håndter tastetrykke.
     * Returnerer sann dersom vi har håndtert tastetrykket.s */
    vedTastetrykk(event: KeyboardEvent): boolean {
        if (event.key === 'ArrowUp') {
            this.aktivTreffIndeks.update(indeks => (--indeks + this.treff().length) % this.treff().length);
            return true;
        }

        if (event.key === 'ArrowDown') {
            this.aktivTreffIndeks.update(indeks => ++indeks % this.treff().length);
            return true;
        }

        if (event.key === 'Enter') {
            let innslag = this.treff()[this.aktivTreffIndeks()];
            switch (this.lenketype()) {
                case 'sak':
                    this.sakValgt(innslag);
                    break;
                case 'dokument':
                    this.dokumentValgt(innslag);
                    break;
                default:
                    this.håndterUkjentLenketype();
            }
            return true;
        }

        return false;
    }

    visDetaljer() {

    }

    //private hjelpemetoder

    private søk(søkestreng: string): Promise<ElasticSearchTreff<any>[]> {
        switch (this.lenketype()) {
            case 'sak':
                return this.avhengigheter.finnSaker(søkestreng, this.gruppeId());
            case 'dokument':
                return this.avhengigheter.finnDokumenter(søkestreng, this.gruppeId());
        }
        this.håndterUkjentLenketype();
        return Promise.resolve([]);
    }

    private lenketekstSak(innslag: ElasticSearchTreff<SakIndeksfelter>): string {
        return this.oversettelser.oversett(i18n('sak {nummer} {tittel} i {møtetittel} {møtenummer}'), {
            nummer: innslag._source.nummer,
            tittel: innslag._source.tittel,
            møtetittel: innslag._source.forelderinfo.moete,
            møtenummer: innslag._source.forelderinfo.moetenummer,
        });
    }

    private lenketekstDokument(innslag: ElasticSearchTreff<OpplastetDokumentVersjonIndeksfelter>): string {
        return erIkkeTomStreng(innslag._source.pdfFilnavn) ? innslag._source.pdfFilnavn : innslag._source.filnavn;
    }

    private håndterUkjentLenketype() {
        if (isDevMode())
            throw new Error(`RiktekstInternLenkeFinnKomponent.søk: ukjent lenketype '${this.lenketype()}' angitt`);
    }
}

@Injectable()
export class InternLenkeKomponentAvhengigheter {

    constructor(private søkRessurs: SøkRessursClient) {
    }

    finnSaker(søkestreng: string, gruppeId: GruppeId): Promise<ElasticSearchTreff<SakIndeksfelter>[]> {
        return this.finn(SøkEntitetType.sak, søkestreng, gruppeId);
    }

    finnDokumenter(søkestreng: string, gruppeId: GruppeId): Promise<ElasticSearchTreff<OpplastetDokumentVersjonIndeksfelter>[]> {
        return this.finn(SøkEntitetType.dokument, søkestreng, gruppeId);
    }

    //private hjelpemetoder

    private finn(entitetType: SøkEntitetType, sokestreng: string, gruppeId: GruppeId): Promise<ElasticSearchTreff<any>[]> {
        let spesifikasjon: SøkEntitetSpesifikasjon = {entitetType, gruppeId, sokestreng};
        return this.søkRessurs.finnEntiteter(spesifikasjon).somPromise()
          .then((resultat: ElasticSearchResultat) => resultat.hits.hits);
    }
}
